import classNames from "classnames";
import { type ChangeEvent, type FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { StylistValue } from "../../../../services/api/instances/stylistApi/stylistTypes";
import { useAppSelector } from "../../../../services/redux/hooks/use-selector";
import { AppButton } from "../../../../shared/components/AppButton";
import { AppLoading } from "../../../../shared/components/AppLoading";
import { AppUnderlinedTitle } from "../../../../shared/components/AppUnderlinedTitle";
import { useModal } from "../../../../shared/hooks/use-modal";
import { ClearIcon } from "../../../../shared/icons/ClearIcon";
import { useStylist } from "../../hook/use-stylist";
import {
  selectSelectedStylist,
  selectStylistDateUpdate,
} from "../../redux/selectors";
import { StylistMarkdownModal } from "../StylistMarkdownModal";
import { StylistTextArea } from "../StylistTextArea";
import { CopyButton } from "../../../../common/Components/Buttons/CopyButton";
import styles from "./styles.module.css";

interface Props {
  isResultsDisabled?: boolean;
  className?: string;
}

export const StylistResult: FC<Props> = ({ isResultsDisabled, className }) => {
  const { handleFieldChange, handleCleanResult, isResultLoading } =
    useStylist();
  const { t } = useTranslation();
  const selectedStylist = useAppSelector(selectSelectedStylist);
  const dateUpdate = useAppSelector(selectStylistDateUpdate);
  const { isModalOpen, showModal, hideModal } = useModal();
  const [minRows, setMinRows] = useState(4);

  useEffect(() => {
    const calculateMinRows = () => {
      const windowHeight = window.innerHeight;
      const rowHeight = 28;
      const rows = Math.floor(windowHeight / rowHeight + 4);
      setMinRows(rows);
    };

    calculateMinRows();

    window.addEventListener("resize", calculateMinRows);

    return () => {
      window.removeEventListener("resize", calculateMinRows);
    };
  }, []);

  return (
    <div className={classNames(styles.result, className)}>
      <AppUnderlinedTitle
        className={styles.title}
        leftContent={t("stylist.result_title")}
      >
        <AppButton
          buttonType={"text"}
          onClick={handleCleanResult}
          className={styles["button-clean"]}
          icon={<ClearIcon />}
        >
          {t("stylist.clear_button")}
        </AppButton>
      </AppUnderlinedTitle>
      <div className={styles["text-area-container"]}>
        <StylistTextArea
          value={selectedStylist?.value?.stylist_object.result as string}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            handleFieldChange(event, "result" as keyof StylistValue, "result");
          }}
          placeholder={t("stylist.result_placeholder")}
          minRows={minRows}
          disabled={isResultsDisabled}
        />
        <CopyButton
          copyContent={selectedStylist?.value?.stylist_object.result}
          position="absolute"
        />
        {isResultLoading && <AppLoading className={styles.loading} big />}
      </div>
      <div className={styles["result-footer"]}>
        <div className={styles.date}>{dateUpdate}</div>
        <AppButton buttonType={"contained"} onClick={showModal}>
          {t("stylist.markdown_button")}
        </AppButton>
      </div>
      <StylistMarkdownModal open={isModalOpen} handleClose={hideModal} />
    </div>
  );
};
